import { useIsClient } from '#/hooks/useIsClient';
import type {
  DateString,
  DatetimeString,
  IanaTimezone,
  TimeString,
} from '#/types/TypedDateString';
import { IDEALIST_TIME_ZONE } from '#/utils/constants/general/idealistTimeZone';

import { dateInstanceToStringParts } from './datetimeStringUtils';

// =====================
// Idealist
// =====================

export function idealistDateAndTimeString(datetime: DatetimeString) {
  return dateInstanceToStringParts(new Date(datetime), {
    timezone: IDEALIST_TIME_ZONE,
  });
}

export function idealistDateString(datetime: DatetimeString) {
  return idealistDateAndTimeString(datetime).date;
}

export function idealistTimeString(datetime: DatetimeString) {
  return idealistDateAndTimeString(datetime).time;
}

// =====================
// Local
// =====================

function localDateAndTimeStrings(datetime: DatetimeString) {
  return dateInstanceToStringParts(new Date(datetime), {
    timezone: null,
  });
}

/**
 * We can only use localDateString client-side, otherwise it causes hydration errors
 */
export function clientOnlyLocalDateString(datetime: DatetimeString) {
  return localDateAndTimeStrings(datetime).date;
}

/**
 * This hook is a utility to help avoid using local date string before hydration by mistake
 */
export function useLocalDateStringFn() {
  const isClient = useIsClient();

  return {
    localDateString: isClient ? clientOnlyLocalDateString : idealistDateString,
  };
}

// =====================
// Given timezone
// =====================

export function offsetDateAndTimeStrings({
  date,
  time,
  fromTimezone,
  toTimezone,
}: {
  date: DateString;
  time: TimeString;
  fromTimezone: IanaTimezone;
  toTimezone: IanaTimezone;
}) {
  // Convert to UTC date
  const zuluDate = new Date(date + 'T' + time);
  const timezoneDate = new Date(
    zuluDate.toLocaleString('en', { timeZone: fromTimezone }),
  );
  const offsetInMs = zuluDate.getTime() - timezoneDate.getTime();

  const utcDatetime = new Date(zuluDate.getTime() + offsetInMs);

  // Return parts
  return dateInstanceToStringParts(utcDatetime, {
    timezone: toTimezone,
  });
}
